/**
 * Dynamic greeting for the home page.
 *
 * Returns two lines:
 * 1. A stable time-of-day greeting with the user's name
 * 2. A rotating quirky/motivational line as JSX for rich styling
 *
 * The quirky line is deterministic per user per day (same user sees the
 * same line all day, different users see different lines).
 */

import { useRef } from 'react';
import type { ReactNode } from 'react';
import { Emphasis as I } from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { getTimeOfDay, type TimeOfDay } from '@/utils/greeting';

interface Greeting {
  /** Time-of-day greeting, optionally with the user's name (e.g. "Good morning, Avi. ☀️" or "Burning the midnight oil. 🔥") */
  hello: string;
  /** JSX tagline with styled fragments */
  tagline: ReactNode;
}

/** Day-specific taglines (only shown on their day). */
const DAY_TAGLINES: Partial<Record<number, ReactNode[]>> = {
  1: [
    // Monday
    <>New week, fresh start.</>,
    <>Monday energy. Let&apos;s build something great.</>,
  ],
  3: [
    // Wednesday
    <>The week is half over. Smooth sailing from here.</>,
  ],
  4: [
    // Thursday
    <>
      Almost Friday. <I>Hang tight.</I>
    </>,
  ],
  5: [
    // Friday
    <>
      You made it to Friday. <I>Enjoy it.</I>
    </>,
    <>
      Go do something fun this weekend. <I>You earned it.</I>
    </>,
  ],
};

/** Anytime taglines — quirky, human, lifecycle marketer solidarity. */
const ANYTIME_TAGLINES: ReactNode[] = [
  <>Your audience awaits.</>,
  <>
    Your audience is <I>lucky</I> to have you.
  </>,
  <>
    Somewhere, someone <I>just</I> opened your email.
  </>,
  <>
    Messages don&apos;t send themselves. <I>Well, actually…</I>
  </>,
  <>Behind every great email is someone like you.</>,
  <>
    Let&apos;s give the spam folder <I>nothing</I> to work with.
  </>,
  <>Another day, another drip.</>,
  <>Ready to hit send?</>,
  <>
    Let&apos;s make something people don&apos;t <I>immediately</I> archive.
  </>,
  <>One good message can change everything.</>,
  <>Now go drink some water.</>,
  <>Don&apos;t lose sleep over email. That&apos;s what we&apos;re here for.</>,
  <>Open rates go up. Open rates go down. You keep showing up.</>,
  <>
    The best marketers obsess over the details. <I>But not too much.</I>
  </>,
  <>
    Whatever&apos;s on your plate, <I>you&apos;ll figure it out.</I> You always do.
  </>,
  <>Take a breath. Then build something great.</>,
  <>
    One thing at a time. <I>You&apos;ve got it.</I>
  </>,
  <>
    Never stop the <I>hackiness.</I>
  </>,
];

/**
 * Internal taglines — only shown to Customer.io employees (@customer.io email).
 * Grounded in actual CIO in-jokes and culture, no generic motivational filler:
 * - Company: Amis, the four values (awkwardness, empathy, transparency,
 *   responsibility), tacos, #favourite-mistakes, the e-i-e-i-o pronunciation bit,
 *   and dogfooding ("we are the customers").
 * - Eng: "GitHub's gonna GitHub", the post-deploy "too quiet", agents wanting more
 *   tokens, and the None/NaN/[object Object] favorite-values gag.
 * - Marketing: open-rate/CTR obsession, holdout tests, CTA above the fold,
 *   the monthly newsletter, and "Planet Customer.io".
 * - Sales: the closed-won ritual, EOM pushes, SKO, and out-competing Braze.
 */
const INTERNAL_TAGLINES: ReactNode[] = [
  // Company culture + values
  <>
    Embrace the awkwardness. <I>It&apos;s a core value.</I>
  </>,
  <>
    Empathy, transparency, responsibility, and a little awkwardness. <I>That&apos;s the job.</I>
  </>,
  <>
    Go give an Ami a taco. <I>They earned it.</I> 🌮
  </>,
  <>Customer dot… e-i-e-i-o.</>,
  <>
    We send the messages <I>and</I> we&apos;re the customers.
  </>,
  <>
    Today&apos;s a good day for a <I>favorite mistake.</I>
  </>,
  <>
    Think… <I>what would Colin do?</I>
  </>,
  <>
    Have you completed your Ethena trainings? <I>Have you?</I>
  </>,
  // Engineering
  <>
    GitHub&apos;s gonna GitHub. <I>You&apos;ll ship anyway.</I>
  </>,
  <>
    It&apos;s quiet today. <I>Too quiet.</I>
  </>,
  <>
    Somewhere, an agent is asking for <I>more tokens.</I>
  </>,
  <>
    May your values never come back <I>NaN.</I>
  </>,
  <>
    Error: undefined is not a function… <I>just kidding, we&apos;re all good.</I>
  </>,
  // Marketing
  <>
    Keep the CTA <I>above the fold.</I>
  </>,
  <>When in doubt, run a holdout test.</>,
  <>Welcome to Planet Customer.io.</>,
  // Sales
  <>
    Another closed-won just hit the channel. <I>Let&apos;s go.</I> 🎉
  </>,
  <>
    Somewhere, a Braze customer is <I>reconsidering.</I>
  </>,
  <>First one to closed-won buys the round at SKO.</>,
];

/** True when the email belongs to a Customer.io employee. */
function isInternalEmail(email: string | null | undefined): boolean {
  return email?.trim().toLowerCase().endsWith('@customer.io') ?? false;
}

/**
 * Pick a pseudorandom item from an array, stable per user per day.
 *
 * Uses (userId × dayOfYear × year) as a seed so:
 * - Different users get different taglines on the same day
 * - The same user gets the same tagline all day
 * - It rotates daily
 */
function pickDaily<T>(items: T[], userId: number, now: Date, salt = 0): T {
  const startOfYear = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));

  const hash = Math.abs(
    ((userId * 2654435761) ^ (dayOfYear * 2246822519) ^ (now.getFullYear() * 13) ^ (salt * 1597334677)) | 0,
  );
  return items[hash % items.length];
}

interface GreetingOptions {
  /** Whether to include emoji in the hello greeting. @defaultValue true */
  withEmoji?: boolean;
  /** User's email — used to unlock internal taglines for @customer.io employees. */
  email?: string | null;
}

/**
 * Hook that returns a stable greeting for the current session.
 * Captures the greeting at mount so it doesn't change mid-session
 * when crossing time-of-day boundaries (noon, 5pm, 10pm).
 */
export function useGreeting({ withEmoji = true }: GreetingOptions = {}): Greeting {
  const { user } = useAuth();
  const greetingRef = useRef(getGreeting(user.first_name, user.id, new Date(), { withEmoji, email: user.email }));
  return greetingRef.current;
}

/** Appends `, Name` before the punctuation when a name is available. */
function withName(
  prefix: string,
  name: string | null,
  punctuation: string,
  emoji: string,
  includeEmoji: boolean,
): string {
  const suffix = includeEmoji ? ` ${emoji}` : '';
  if (name != null) {
    return `${prefix}, ${name}${punctuation}${suffix}`;
  }
  return `${prefix}${punctuation}${suffix}`;
}

export function getGreeting(
  firstName: string,
  userId: number,
  now: Date = new Date(),
  { withEmoji = true, email }: GreetingOptions = {},
): Greeting {
  const trimmed = firstName.trim();
  const name = trimmed.length > 0 ? trimmed : null;
  const timeOfDay = getTimeOfDay(now.getHours());
  const greetingPools: Record<TimeOfDay, ((n: string | null) => string)[]> = {
    morning: [
      (n) => withName('Good morning', n, '.', '☀️', withEmoji),
      (n) => withName('Rise and shine', n, '.', '☀️', withEmoji),
      (n) => withName('Morning', n, '.', '☀️', withEmoji),
    ],
    afternoon: [
      (n) => withName('Good afternoon', n, '.', '🌤', withEmoji),
      (n) => withName('Afternoon', n, '.', '🌤', withEmoji),
      (n) => withName('Hope your day is going well', n, '.', '🌤', withEmoji),
    ],
    evening: [
      (n) => withName('Good evening', n, '.', '🌆', withEmoji),
      (n) => withName('Evening', n, '.', '🌆', withEmoji),
      (n) => withName('Winding down', n, '.', '🌆', withEmoji),
    ],
    night: [
      (n) => withName('Burning the midnight oil', n, '.', '🔥', withEmoji),
      (n) => withName('Up late', n, '?', '🦉', withEmoji),
      (n) => withName('The night is young', n, '.', '🌙', withEmoji),
      (n) => withName('Why are you still online', n, '?', '🌙', withEmoji),
    ],
  };
  const greetingFunction = pickDaily(greetingPools[timeOfDay], userId, now, 1);
  const hello = greetingFunction(name);

  // Build the tagline pool: day-specific + anytime (+ internal for employees)
  const pool: ReactNode[] = [...ANYTIME_TAGLINES];

  if (isInternalEmail(email)) {
    pool.push(...INTERNAL_TAGLINES);
  }

  const dayOfWeek = now.getDay();
  const dayPool = DAY_TAGLINES[dayOfWeek];
  if (dayPool != null) {
    // Add day-specific ones with higher weight (3x)
    for (const tagline of dayPool) {
      pool.push(tagline, tagline, tagline);
    }
  }

  const tagline = pickDaily(pool, userId, now);

  return { hello, tagline };
}
